import React, { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { useSuspendPollingWhileOpen } from "@/lib/dialogPolling";
import { toast } from "sonner";
import { X, Droplets, Utensils, ChevronRight, ChevronDown } from "lucide-react";
import { fmtTime } from "@/lib/time";
import PetCareScheduleBlock from "@/components/PetCareScheduleBlock";
import { isPetWalkLockedForPet, nextTierForRank } from "@/lib/petCareGate";

// Format an ISO time as HH:mm in local tz
const fmtHM = (iso) => fmtTime(iso) || "";

// Given "HH:mm" string + today's date → ISO at that local time.
// If `future` is true and the picked time is earlier than now, it rolls to tomorrow.
const hmToIso = (hm, { future = false } = {}) => {
  if (!hm) return null;
  const [h, m] = hm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  if (future && d.getTime() <= Date.now()) {
    d.setDate(d.getDate() + 1);
  }
  return d.toISOString();
};

const nowHM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export default function PetDialog({ pet, existing, members, viewerId, onClose, onSaved, onKeepOpen, onPetUpdated, tier, onOpenUpgrade }) {
  useSuspendPollingWhileOpen();
  const nonPets = useMemo(
    () => (members || []).filter((m) => m.role !== "pet"),
    [members]
  );
  const [walker, setWalker] = useState(
    viewerId && nonPets.some((m) => m.id === viewerId) ? viewerId : nonPets[0]?.id || ""
  );
  const [otherActive, setOtherActive] = useState(false);
  const [otherLabel, setOtherLabel] = useState("");
  // Leave empty so the dialog doesn't look like it's about to log a walk.
  // If the user taps "Log walk" without touching the time, we default to
  // `nowHM()` inside the save() handler.
  const [walkTime, setWalkTime] = useState("");
  const [nextTime, setNextTime] = useState(() => {
    if (!existing?.next_walk_at) return "";
    try {
      const d = new Date(existing.next_walk_at);
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  });
  const [nextWalker, setNextWalker] = useState(
    existing?.next_walk_by
      ? existing.next_walk_by
      : existing?.next_walk_by_label
      ? "__other__"
      : ""
  );
  const [nextOtherLabel, setNextOtherLabel] = useState(existing?.next_walk_by_label || "");
  // One more upcoming walk after "Next" — same shape.
  const [afterTime, setAfterTime] = useState(() => {
    if (!existing?.after_next_walk_at) return "";
    try {
      const d = new Date(existing.after_next_walk_at);
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  });
  const [afterWalker, setAfterWalker] = useState(
    existing?.after_next_walk_by
      ? existing.after_next_walk_by
      : existing?.after_next_walk_by_label
      ? "__other__"
      : ""
  );
  const [afterOtherLabel, setAfterOtherLabel] = useState(existing?.after_next_walk_by_label || "");
  const [saving, setSaving] = useState(false);
  // Plan-a-future-walk section starts collapsed so the dialog reads as
  // "log a walk → done" by default. Auto-expands when the family already
  // has a plan saved (so editing it stays one tap away). Removes the
  // biggest source of clutter the user flagged in the screenshot.
  const [planOpen, setPlanOpen] = useState(
    !!(existing?.next_walk_at || existing?.after_next_walk_at),
  );

  // Walk-tier gate — same cap as Pet Care Schedule. The oldest dog/cat
  // is always covered; additional dogs/cats are locked until the family
  // upgrades. Feeds + water stay free for everyone, so we only swap out
  // the walk-logging UI, not the whole dialog.
  const walkLocked = isPetWalkLockedForPet(pet, members, tier);
  const upgradeTo = (() => {
    const t = (tier || "free").toLowerCase();
    if (t === "free") return "Plus";
    if (t === "plus") return "Grand";
    return "Extended";
  })();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const walks = (existing?.walks || []).slice().sort((a, b) => (a.at > b.at ? -1 : 1));
  const lastWalk = walks[0];
  const lastWalker = lastWalk && lastWalk.by
    ? (members || []).find((m) => m.id === lastWalk.by)
    : null;
  const lastWalkerLabel = lastWalk
    ? (lastWalker?.name || lastWalk.by_label || "someone")
    : "";

  const save = async () => {
    if (otherActive) {
      if (!otherLabel.trim()) {
        toast.error("Type who walked");
        return;
      }
    } else if (!walker) {
      toast.error("Pick who walked");
      return;
    }
    setSaving(true);
    try {
      const nextOtherActive = nextWalker === "__other__";
      const afterOtherActive = afterWalker === "__other__";
      // If the user didn't touch the Walk time input, default to "now".
      const effectiveWalkTime = walkTime || nowHM();
      const updated = await api.logPetWalk({
        pet_id: pet.id,
        walked_by: otherActive ? null : walker,
        walked_by_label: otherActive ? otherLabel.trim() : undefined,
        at: hmToIso(effectiveWalkTime) || undefined,
        next_walk_at: nextTime ? hmToIso(nextTime, { future: true }) : undefined,
        next_walk_by: nextWalker && !nextOtherActive ? nextWalker : null,
        next_walk_by_label: nextOtherActive ? (nextOtherLabel.trim() || null) : null,
        after_next_walk_at: afterTime ? hmToIso(afterTime, { future: true }) : undefined,
        after_next_walk_by: afterWalker && !afterOtherActive ? afterWalker : null,
        after_next_walk_by_label: afterOtherActive ? (afterOtherLabel.trim() || null) : null,
      });
      toast.success("Walk logged");
      onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const nextOtherActive = nextWalker === "__other__";
      const afterOtherActive = afterWalker === "__other__";
      const updated = await api.setPetNextWalk({
        pet_id: pet.id,
        next_walk_at: nextTime ? hmToIso(nextTime, { future: true }) : null,
        next_walk_by: nextWalker && !nextOtherActive ? nextWalker : null,
        next_walk_by_label: nextOtherActive ? (nextOtherLabel.trim() || null) : null,
        after_next_walk_at: afterTime ? hmToIso(afterTime, { future: true }) : null,
        after_next_walk_by: afterWalker && !afterOtherActive ? afterWalker : null,
        after_next_walk_by_label: afterOtherActive ? (afterOtherLabel.trim() || null) : null,
      });
      toast.success("Plan updated");
      onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="pet-dialog"
    >
      <div
        className="relative bg-[color:var(--bg)] rounded-t-[28px] md:rounded-[20px] w-full md:max-w-xl max-h-[92svh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[color:var(--bg)] pl-6 pr-4 pt-5 pb-3 flex items-center justify-between border-b border-[color:var(--line-soft)] z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center text-[26px] flex-shrink-0"
              style={{ background: "#EADFCB" }}
            >
              {pet.avatar_emoji}
            </div>
            <div className="min-w-0">
              <div className="font-display text-xl leading-none truncate">
                {pet.name}
              </div>
              <div className="label-tiny mt-1">Log a walk</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
              data-testid="pet-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Water change — available on dogs too (water bowl refresh).
              Uses onKeepOpen so the dialog stays open: water and feed are
              usually logged in the same trip to the kitchen, and bouncing
              back to the dashboard between them is friction the user
              doesn't need. */}
          <DogWaterRow
            pet={pet}
            existing={existing}
            viewerId={viewerId}
            nonPets={nonPets}
            onSaved={onKeepOpen || onSaved}
          />
          {/* Fed — dogs get double-fed all the time; visibility prevents
              that. Same keep-open rationale as water. */}
          <DogFedRow
            pet={pet}
            existing={existing}
            viewerId={viewerId}
            nonPets={nonPets}
            onSaved={onKeepOpen || onSaved}
          />
          {/* Today's walks summary row — sized to match the WATER BOWL
              and FED rows so the three rows read as one consistent
              vertical rhythm. Earlier this was a tiny `label-tiny`
              caption and felt drowned next to the display-text rows
              above. The walk-count + last time still live here for
              quick reference, but full per-walk history lives only on
              the dog's pet card on /app/pets — same flow as Skylight
              (less is more). */}
          {walks.length > 0 && (
            <div data-testid="pet-today-summary">
              <div className="label-tiny mb-1">Today</div>
              <div className="font-display-italic text-lg leading-snug text-[color:var(--ink-2)]">
                {walks.length} {walks.length === 1 ? "walk" : "walks"}
                {" · "}last {fmtHM(walks[0].at)}
                {existing?.next_walk_at && (
                  <> · next {fmtHM(existing.next_walk_at)}</>
                )}
                {" · "}
                <a
                  href="/app/pets"
                  className="underline decoration-[color:var(--ink-3)]/40 underline-offset-2 hover:decoration-[color:var(--ink)] text-[color:var(--ink-2)]"
                  data-testid="pet-today-view-more"
                >
                  View more
                </a>
              </div>
            </div>
          )}

          {walkLocked ? (
            <div
              className="ff-card p-5 text-center space-y-3 border-2 border-dashed border-[color:var(--accent)]/40"
              data-testid="pet-walk-locked-card"
            >
              <div className="font-display text-xl">
                {pet.name} is on the upgrade list
              </div>
              <p className="text-[14px] text-[color:var(--ink-2)] leading-snug max-w-sm mx-auto">
                Your plan covers walk logging for one dog or cat. Upgrade
                to {upgradeTo} to log walks, plan next walks and send
                walker reminders for {pet.name} too.
              </p>
              <button
                type="button"
                onClick={() => onOpenUpgrade && onOpenUpgrade()}
                className="ff-btn ff-btn-primary"
                data-testid="pet-walk-upgrade-btn"
              >
                Upgrade to {upgradeTo}
              </button>
              <div className="label-tiny text-[color:var(--ink-3)]">
                Feeds and fresh water stay free for every pet.
              </div>
            </div>
          ) : (<>
          <div>
            <label className="label-tiny block mb-2">Walked by</label>
            <div className="flex flex-wrap gap-1.5">
              {nonPets.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    setWalker(m.id);
                    setOtherActive(false);
                  }}
                  className={`ff-chip ${walker === m.id && !otherActive ? "is-active" : ""}`}
                  data-testid={`pet-walker-${m.id}`}
                >
                  <span className="text-base">{m.avatar_emoji}</span>
                  {m.name}
                </button>
              ))}
              <button
                onClick={() => setOtherActive(true)}
                className={`ff-chip ${otherActive ? "is-active" : ""}`}
                data-testid="pet-walker-other"
              >
                Other
              </button>
            </div>
            {otherActive && (
              <input
                type="text"
                value={otherLabel}
                onChange={(e) => setOtherLabel(e.target.value)}
                placeholder="e.g. Neighbor Erik"
                maxLength={40}
                className="ff-input mt-2 max-w-xs"
                data-testid="pet-walker-other-input"
                autoFocus
              />
            )}
          </div>

          {/* Walk time — primary action input. Single column so it
              reads as the focused field, not buried in a half-empty
              two-column grid alongside future planning. The plan-next-
              walk section moved into a collapsible disclosure below. */}
          <div>
            <label className="label-tiny block mb-2">Walk time</label>
            <input
              type="time"
              value={walkTime}
              onChange={(e) => setWalkTime(e.target.value)}
              className="ff-input tabular w-full sm:max-w-[220px]"
              data-testid="pet-walk-time"
            />
            <div className="label-tiny mt-1.5 text-[color:var(--ink-3)]">
              Leave blank to log "now".
            </div>
          </div>

          {/* Plan-a-next-walk disclosure. Default closed unless a plan
              already exists. Hides ~120px of next-walk + after-that
              chips behind a single-line toggle so the form reads as a
              quick "log a walk" by default. */}
          <div className="border-t border-[color:var(--line-soft)] pt-4">
            <button
              type="button"
              onClick={() => setPlanOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left text-sm text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
              data-testid="pet-plan-toggle"
              aria-expanded={planOpen}
            >
              <span className="inline-flex items-center gap-2">
                {planOpen ? (
                  <ChevronDown className="w-4 h-4" strokeWidth={2} />
                ) : (
                  <ChevronRight className="w-4 h-4" strokeWidth={2} />
                )}
                Plan a next walk
                {(nextTime || afterTime) && !planOpen && (
                  <span className="label-tiny text-[color:var(--ink-3)] ml-1">
                    · {nextTime ? fmtHM(hmToIso(nextTime, { future: true })) : ""}
                    {afterTime ? ` · ${fmtHM(hmToIso(afterTime, { future: true }))}` : ""}
                  </span>
                )}
              </span>
              <span className="label-tiny text-[color:var(--ink-3)]">
                {planOpen ? "hide" : "optional"}
              </span>
            </button>
            {planOpen && (
            <div className="mt-4 space-y-4" data-testid="pet-plan-section">
              <div>
                <label className="label-tiny block mb-2">Next walk time</label>
                <input
                  type="time"
                  value={nextTime}
                  onChange={(e) => setNextTime(e.target.value)}
                  className="ff-input tabular w-full sm:max-w-[220px]"
                  data-testid="pet-next-walk-time"
                />
              </div>

              {/* Next walker picker */}
              <div>
                <label className="label-tiny block mb-2">Next walker (optional)</label>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setNextWalker("")}
                    className={`ff-chip ${nextWalker === "" ? "is-active" : ""}`}
                    data-testid="pet-nextwalker-none"
                  >
                    Nobody yet
                  </button>
                  {nonPets.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setNextWalker(m.id)}
                      className={`ff-chip ${nextWalker === m.id ? "is-active" : ""}`}
                      data-testid={`pet-nextwalker-${m.id}`}
                    >
                      <span className="mr-1">{m.avatar_emoji}</span>
                      {m.name}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNextWalker("__other__")}
                    className={`ff-chip ${nextWalker === "__other__" ? "is-active" : ""}`}
                    data-testid="pet-nextwalker-other"
                  >
                    Other
                  </button>
                </div>
                {nextWalker === "__other__" && (
                  <input
                    type="text"
                    value={nextOtherLabel}
                    onChange={(e) => setNextOtherLabel(e.target.value)}
                    maxLength={40}
                    placeholder="Who? (e.g. Granny, Dog-walker)"
                    className="ff-input mt-2"
                    data-testid="pet-nextwalker-other-input"
                  />
                )}
                {/* Reminder hint: surfaces only once the schedule is concrete
                    enough to trigger the server-side 30-min push. */}
                {nextTime && nextWalker && nextWalker !== "__other__" && (
                  <div
                    className="label-tiny mt-3 text-[color:var(--ink-3)] leading-snug"
                    data-testid="pet-nextwalk-reminder-hint"
                  >
                    🔔 {nonPets.find((m) => m.id === nextWalker)?.name || "They"}'ll
                    get a push reminder 30 minutes before — if notifications are on.
                  </div>
                )}

                {/* One more upcoming walk after "Next" — shown once a next walk is scheduled. */}
                {(nextTime || afterTime) && (
                  <div className="mt-5 pt-5 border-t border-[color:var(--line-soft)]" data-testid="pet-after-section">
                    <div className="label-tiny mb-2">One more after that (optional)</div>
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="time"
                        value={afterTime}
                        onChange={(e) => setAfterTime(e.target.value)}
                        className="ff-input tabular max-w-[160px]"
                        data-testid="pet-after-walk-time"
                      />
                      {afterTime && (
                        <button
                          type="button"
                          onClick={() => {
                            setAfterTime("");
                            setAfterWalker("");
                            setAfterOtherLabel("");
                          }}
                          className="text-sm text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
                          data-testid="pet-after-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {afterTime && (
                      <>
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => setAfterWalker("")}
                            className={`ff-chip ${afterWalker === "" ? "is-active" : ""}`}
                            data-testid="pet-afterwalker-none"
                          >
                            Nobody yet
                          </button>
                          {nonPets.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => setAfterWalker(m.id)}
                              className={`ff-chip ${afterWalker === m.id ? "is-active" : ""}`}
                              data-testid={`pet-afterwalker-${m.id}`}
                            >
                              <span className="mr-1">{m.avatar_emoji}</span>
                              {m.name}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => setAfterWalker("__other__")}
                            className={`ff-chip ${afterWalker === "__other__" ? "is-active" : ""}`}
                            data-testid="pet-afterwalker-other"
                          >
                            Other
                          </button>
                        </div>
                        {afterWalker === "__other__" && (
                          <input
                            type="text"
                            value={afterOtherLabel}
                            onChange={(e) => setAfterOtherLabel(e.target.value)}
                            maxLength={40}
                            placeholder="Who? (e.g. Granny, Dog-walker)"
                            className="ff-input mt-2"
                            data-testid="pet-afterwalker-other-input"
                          />
                        )}
                        {/* Mirror of the "Next walk" reminder hint — surfaces
                            only once the second walk is concrete enough to
                            trigger its own server-side 30-min push. The
                            backend already schedules walk reminders off
                            `walk_iso` AND `walk_iso_after`, so the assigned
                            walker for the second slot also gets a ping. */}
                        {afterTime && afterWalker && afterWalker !== "__other__" && (
                          <div
                            className="label-tiny mt-3 text-[color:var(--ink-3)] leading-snug"
                            data-testid="pet-afterwalk-reminder-hint"
                          >
                            🔔 {nonPets.find((m) => m.id === afterWalker)?.name || "They"}'ll
                            get a push reminder 30 minutes before — if notifications are on.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    onClick={savePlan}
                    disabled={saving}
                    className="ff-btn"
                    data-testid="pet-save-plan"
                  >
                    {saving ? "Saving…" : "Save plan only"}
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="ff-btn"
              data-testid="pet-cancel"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="ff-btn ff-btn-primary"
              data-testid="pet-save"
            >
              {saving ? "Saving…" : "Log walk"}
            </button>
          </div>
          </>)}
          <PetCareScheduleBlock
            pet={pet}
            members={members}
            tier={tier}
            onPetUpdated={onPetUpdated}
            onOpenUpgrade={onOpenUpgrade}
          />
        </div>
      </div>
    </div>
  );
}


/**
 * DogWaterRow — small inline row inside PetDialog (dogs) showing "last water
 * change" + a single "Fresh water" button. Kept compact so it doesn't
 * compete with the primary walk-logging flow.
 */
function DogWaterRow({ pet, existing, viewerId, nonPets, onSaved }) {
  const [busy, setBusy] = useState(false);
  const waterAt = existing?.water_change_at;
  const waterByName = existing?.water_change_by
    ? (nonPets.find((m) => m.id === existing.water_change_by)?.name)
    : (existing?.water_change_by_label || null);

  const onClick = async () => {
    const actor =
      viewerId && nonPets.some((m) => m.id === viewerId)
        ? viewerId
        : nonPets[0]?.id || null;
    if (!actor) {
      toast.error("Add a family member first");
      return;
    }
    setBusy(true);
    try {
      const updated = await api.logPetCare({
        pet_id: pet.id,
        action: "water",
        by: actor,
      });
      onSaved && onSaved(updated);
      toast.success(`Water bowl refreshed for ${pet.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="label-tiny">Water bowl</div>
        <div className="font-display-italic text-lg leading-snug text-[color:var(--ink-2)] truncate">
          {waterAt ? (
            <>
              Changed {fmtTime(waterAt)}
              {waterByName ? ` · by ${waterByName}` : ""}
            </>
          ) : (
            "Not changed today"
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="ff-btn inline-flex items-center gap-2"
        data-testid="pet-water-btn"
      >
        <Droplets className="w-4 h-4" strokeWidth={2} />
        {busy ? "Saving…" : "Fresh water"}
      </button>
    </div>
  );
}


/**
 * DogFedRow — mirrors DogWaterRow but for feeds. Dogs need feeding too;
 * this gives the whole family visibility on who already fed the dog so
 * Buddy doesn't end up with double-dinners. Same compact single-row
 * layout as water to keep the dialog tidy.
 */
function DogFedRow({ pet, existing, viewerId, nonPets, onSaved }) {
  const [busy, setBusy] = useState(false);
  const fedAt = existing?.fed_at;
  const fedByName = existing?.fed_by
    ? (nonPets.find((m) => m.id === existing.fed_by)?.name)
    : (existing?.fed_by_label || null);

  const onClick = async () => {
    const actor =
      viewerId && nonPets.some((m) => m.id === viewerId)
        ? viewerId
        : nonPets[0]?.id || null;
    if (!actor) {
      toast.error("Add a family member first");
      return;
    }
    setBusy(true);
    try {
      const updated = await api.logPetCare({
        pet_id: pet.id,
        action: "fed",
        by: actor,
      });
      onSaved && onSaved(updated);
      toast.success(`${pet.name} just fed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="label-tiny">Fed</div>
        <div className="font-display-italic text-lg leading-snug text-[color:var(--ink-2)] truncate">
          {fedAt ? (
            <>
              Fed {fmtTime(fedAt)}
              {fedByName ? ` · by ${fedByName}` : ""}
            </>
          ) : (
            "Not fed today"
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="ff-btn inline-flex items-center gap-2"
        data-testid="pet-fed-btn"
      >
        <Utensils className="w-4 h-4" strokeWidth={2} />
        {busy ? "Saving…" : "Just fed"}
      </button>
    </div>
  );
}
